import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddIcon from "../../../assets/add.svg?react";
import TrashCanIcon from "../../../assets/trash-can.svg?react";
import { AppContext, LighthouseContext } from "../../../Context";
import { StoreState } from "../../../store/rootReducer";
import { getMapView, highlightFeature, unHighlightGraphic } from "../../../utils/arcgisUtils";
import { metersPerNauticalMile } from "../../../utils/constants";
import { DefaultSectorValues, SectorColor } from "../../../utils/lighthouseConstants";
import {
    createSector,
    deleteSector,
    isLighthouse,
    removeLighthouseEditingOverlay,
    sanitizeSectors,
} from "../../../utils/lighthouseUtils";
import Collapsible from "../../genericComponents/Collapsible/Collapsible";
import NfsButton from "../../genericComponents/NfsButton/NfsButton";
import Spacer from "../../genericComponents/Spacer/Spacer";
import SectorColorAttribute from "./SectorColorAttribute";
import SectorColorIndicator from "./SectorColorIndicator";
import SectorDescriptionAttribute from "./SectorDescriptionAttribute";
import SectorNumberAttribute from "./SectorNumberAttribute";
import { getNewStatus, NFSStatus } from "../../../utils/nfsUtils";
import { setHasUnsavedChanges } from "../../../store/appSlice";
import ConfirmationPopover from "../../genericComponents/confirmationPopover/confirmationPopover";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

interface SectorMenuProps {
    sectorIndex: number;
    displayIndex: number;
    unitSelectedIndex: number;
}

const SectorMenu = (props: SectorMenuProps) => {
    const { sectorIndex, displayIndex, unitSelectedIndex } = props;
    const dispatch = useDispatch();
    const highlightSectorHandleRef = useRef<ResourceHandle | void>(undefined);
    const highlightSectorRoseHandleRef = useRef<ResourceHandle | void>(undefined);
    const highlightSectorLineHoverHandleRef = useRef<ResourceHandle | void>(undefined);
    const highlightSectorLineEditingHandleRef = useRef<ResourceHandle | void>(undefined);
    const highlightSectorLineHoverRoseHandleRef = useRef<ResourceHandle | void>(undefined);
    const highlightSectorLineEditingRoseHandleRef = useRef<ResourceHandle | void>(undefined);
    const [highlightSectorLineHoverIndex, setHighlightSectorLineHoverIndex] = useState<number | null>(null);
    const [highlightSectorLineEditingIndex, setHighlightSectorLineEditingIndex] = useState<number | null>(null);

    const hasUnsavedChanges = useSelector((state: StoreState) => state.app.hasUnsavedChanges);

    const appContext = useContext(AppContext);
    const selectedFeature = appContext?.selectedFeature.value;
    const lighthouseContext = useContext(LighthouseContext);
    const lighthouseData = lighthouseContext!.lighthouseData.value;

    const isDrawing = useSelector((state: StoreState) => state.lighthouse.isDrawing);

    const sector = lighthouseData.sectors[sectorIndex];
    const sectorNext =
        lighthouseData.sectors[(sectorIndex + 1) % lighthouseData.sectors.length] ?? lighthouseData.sectors[0];
    const setLighthouseState = lighthouseContext!.lighthouseData.set;

    const isOpen = lighthouseContext!.lighthouseSectorPanelsOpen.value.has(sectorIndex);

    const setIsOpen = (sectorIndex: number, isOpen: boolean) => {
        const openSectors = lighthouseContext?.lighthouseSectorPanelsOpen;
        if (isOpen) {
            openSectors?.set(new Set(openSectors.value).add(sectorIndex));
        } else {
            const newSectors = new Set(openSectors?.value);
            newSectors.delete(sectorIndex);
            openSectors?.set(newSectors);
        }
    };

    const setSelectedColorOption = (option: string | null) => {
        if (!hasUnsavedChanges) {
            dispatch(setHasUnsavedChanges(true));
        }
        const newLighthouseData = { ...lighthouseData };
        if (option) {
            newLighthouseData.sectors[sectorIndex].color = SectorColor[option as keyof typeof SectorColor];
            newLighthouseData.sectors[sectorIndex].nfs_status = getNewStatus(
                newLighthouseData.sectors[sectorIndex].nfs_status,
                NFSStatus.PROJECTED_EXISTING
            );
        }
        lighthouseContext!.lighthouseData.value = newLighthouseData;

        setLighthouseState(lighthouseContext!.lighthouseData.value);
    };

    const setLineAngle = (value: number, sectorIndex: number) => {
        if (!hasUnsavedChanges) {
            dispatch(setHasUnsavedChanges(true));
        }
        const newLighthouseData = { ...lighthouseData };
        (newLighthouseData.sectors[sectorIndex] as any).startAngle = value;
        newLighthouseData.sectors[sectorIndex].nfs_status = getNewStatus(
            newLighthouseData.sectors[sectorIndex].nfs_status,
            NFSStatus.PROJECTED_EXISTING
        );

        newLighthouseData.sectors[sectorIndex].nfs_status_line1 = getNewStatus(
            newLighthouseData.sectors[sectorIndex].nfs_status_line1,
            NFSStatus.PROJECTED_EXISTING
        );

        const sectorPrevIndex = (sectorIndex - 1 + lighthouseData.sectors.length) % lighthouseData.sectors.length;
        newLighthouseData.sectors[sectorPrevIndex].nfs_status = getNewStatus(
            newLighthouseData.sectors[sectorPrevIndex].nfs_status,
            NFSStatus.PROJECTED_EXISTING
        );
        newLighthouseData.sectors[sectorPrevIndex].nfs_status_line2 = getNewStatus(
            newLighthouseData.sectors[sectorPrevIndex].nfs_status_line2,
            NFSStatus.PROJECTED_EXISTING
        );

        lighthouseContext!.lighthouseData.value = newLighthouseData;

        setLighthouseState(lighthouseContext!.lighthouseData.value);
    };

    const setLineLength = (value: number, sectorIndex: number) => {
        if (!hasUnsavedChanges) {
            dispatch(setHasUnsavedChanges(true));
        }
        const newLighthouseData = { ...lighthouseData };

        (newLighthouseData.sectors[sectorIndex] as any).startLineLength = value;
        newLighthouseData.sectors[sectorIndex].nfs_status_line1 = getNewStatus(
            newLighthouseData.sectors[sectorIndex].nfs_status_line1,
            NFSStatus.PROJECTED_EXISTING
        );

        const sectorPrevIndex = (sectorIndex - 1 + lighthouseData.sectors.length) % lighthouseData.sectors.length;
        newLighthouseData.sectors[sectorPrevIndex].nfs_status_line2 = getNewStatus(
            newLighthouseData.sectors[sectorPrevIndex].nfs_status_line2,
            NFSStatus.PROJECTED_EXISTING
        );

        lighthouseContext!.lighthouseData.value = newLighthouseData;

        setLighthouseState(lighthouseContext!.lighthouseData.value);
    };

    const setSectorValue = (value: number | string, sectorIndex: number, attributeName: string) => {
        if (!hasUnsavedChanges) {
            dispatch(setHasUnsavedChanges(true));
        }
        const newLighthouseData = { ...lighthouseData };
        (newLighthouseData.sectors[sectorIndex] as any)[attributeName] = value;
        newLighthouseData.sectors[sectorIndex].nfs_status = getNewStatus(
            newLighthouseData.sectors[sectorIndex].nfs_status,
            NFSStatus.PROJECTED_EXISTING
        );

        lighthouseContext!.lighthouseData.value = newLighthouseData;
        setLighthouseState(lighthouseContext!.lighthouseData.value);
    };

    const isAngleLessThanOneDegree = (startAngle1: number, startAngle2: number) => {
        return Math.abs(startAngle2 - startAngle1) < 1;
    };

    const findLineGraphic = (index: number, mapViewId?: string, layerId?: string) => {
        const mapView = getMapView(mapViewId);
        if (!mapView?.map) return;

        const lighthouseEditingLayer = mapView.map.findLayerById(layerId ?? "FyrlyktEditing") as GraphicsLayer;
        const lineGraphic = lighthouseEditingLayer.graphics.find(
            (graphic) => graphic.attributes?.objectType === "line" && graphic.attributes?.index === index
        );

        return lineGraphic;
    };

    const highlightLineGraphic = (
        index: number,
        mapRef: React.RefObject<void | ResourceHandle>,
        mapViewId?: string,
        layerId?: string
    ) => {
        const mapView = getMapView(mapViewId);
        if (!mapView?.map) return;

        const layer = mapView.map.findLayerById(layerId ?? "FyrlyktEditing") as GraphicsLayer;
        const lineGraphic = findLineGraphic(index, mapViewId, layerId);

        if (lineGraphic) {
            highlightFeature(lineGraphic, "default", layer, mapViewId).then((handle) => {
                mapRef.current = handle;
            });
        }
    };

    const unHighlightLineGraphic = (
        index: number,
        ref: React.RefObject<void | ResourceHandle>,
        mapViewId?: string,
        layerId?: string
    ) => {
        const mapView = getMapView(mapViewId);
        if (!mapView?.map) return;

        const layer = mapView.map.findLayerById(layerId ?? "FyrlyktEditing") as GraphicsLayer;
        const lineGraphic = findLineGraphic(index);

        if (lineGraphic) {
            if (!(highlightSectorLineEditingHandleRef.current && highlightSectorLineHoverHandleRef.current)) {
                unHighlightGraphic(lineGraphic, layer);
            }
            ref.current?.remove();
            ref.current = undefined;
        }
    };

    const onBlur = (index?: number) => {
        index ??= sectorIndex;

        const newData = sanitizeSectors(lighthouseData, index);

        for (const [i, sector] of newData.sectors.entries()) {
            sector.index = i;
        }
        lighthouseContext!.lighthouseData.value = newData;
        setLighthouseState(lighthouseContext!.lighthouseData.value);
        selectedFeature!.attributes.lighthouseData = newData;
        appContext!.selectedFeature.set(selectedFeature!);

        unHighlightLineGraphic(index, highlightSectorLineEditingHandleRef);
        unHighlightLineGraphic(index, highlightSectorLineEditingRoseHandleRef, "sector-rose-map", "SectorRose");
        setHighlightSectorLineEditingIndex(null);
    };

    const onFocus = (index?: number) => {
        index ??= sectorIndex;
        highlightLineGraphic(index, highlightSectorLineEditingHandleRef);
        highlightLineGraphic(index, highlightSectorLineEditingRoseHandleRef, "sector-rose-map", "SectorRose");
        setHighlightSectorLineEditingIndex(index);
    };

    const onMouseEnter = (index?: number) => {
        index ??= sectorIndex;
        highlightLineGraphic(index, highlightSectorLineHoverHandleRef);
        highlightLineGraphic(index, highlightSectorLineHoverRoseHandleRef, "sector-rose-map", "SectorRose");
        setHighlightSectorLineHoverIndex(index);
    };

    const onMouseLeave = (index?: number) => {
        index ??= sectorIndex;
        unHighlightLineGraphic(index, highlightSectorLineHoverHandleRef);
        unHighlightLineGraphic(index, highlightSectorLineHoverRoseHandleRef, "sector-rose-map", "SectorRose");
        setHighlightSectorLineHoverIndex(null);
    };

    const highlightSector = (ref: React.RefObject<void | ResourceHandle>, mapViewId?: string, layerId?: string) => {
        const mapView = getMapView(mapViewId);
        if (!mapView?.map) return;

        const layer = mapView.map.findLayerById(layerId ?? "FyrlyktEditing") as GraphicsLayer;
        const sectorGraphics = layer.graphics.filter((graphic) => graphic.attributes?.objectType === "sector");

        const targetGraphic = sectorGraphics.find((graphic) => graphic.attributes?.index === sectorIndex);

        if (targetGraphic) {
            highlightFeature(targetGraphic, "default", layer, mapViewId).then((handle) => {
                ref.current = handle;
            });
        }
    };

    const unHighlightSector = (ref: React.RefObject<void | ResourceHandle>, mapViewId?: string, layerId?: string) => {
        const mapView = getMapView(mapViewId);
        if (!mapView?.map) return;

        const layer = mapView.map.findLayerById(layerId ?? "FyrlyktEditing") as GraphicsLayer;
        const sectorGraphics = layer.graphics.filter((graphic) => graphic.attributes?.objectType === "sector");

        const targetGraphic = sectorGraphics.find((graphic) => graphic.attributes?.index === sectorIndex);
        if (targetGraphic) {
            unHighlightGraphic(targetGraphic, layer);
        }
        ref.current?.remove();
        ref.current = undefined;
    };

    useEffect(() => {
        // If data changes and a line is highlighted, reapply the highlight
        if (highlightSectorLineHoverIndex !== null) {
            setTimeout(() => {
                onMouseEnter(highlightSectorLineHoverIndex);
            }, 100);
        }
        if (highlightSectorLineEditingIndex !== null) {
            setTimeout(() => {
                onFocus(highlightSectorLineEditingIndex);
            }, 100);
        }
    }, [lighthouseContext?.lighthouseData.value]);

    // Effect to handle highlighting when lighthouse data changes or panel opens/closes
    useEffect(() => {
        const mapView = getMapView();
        if (!mapView || isDrawing) {
            highlightSectorHandleRef.current?.remove();
            highlightSectorHandleRef.current = undefined;
            return;
        }

        if (isOpen) {
            setTimeout(() => {
                highlightSector(highlightSectorHandleRef);
                highlightSector(highlightSectorRoseHandleRef, "sector-rose-map", "SectorRose");
            }, 100); // We need this delay in order to wait for the graphic to be added to the layer
        } else {
            setTimeout(() => {
                unHighlightSector(highlightSectorHandleRef);
                unHighlightSector(highlightSectorRoseHandleRef, "sector-rose-map", "SectorRose");
            }, 100);
        }

        return () => {
            highlightSectorHandleRef.current?.remove();
            highlightSectorHandleRef.current = undefined;
        };
    }, [lighthouseContext?.lighthouseData.value, isOpen, isDrawing]);

    useEffect(() => {
        if (isOpen) {
            const element = document.getElementById(`sector-${sectorIndex}`);
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }
    }, [isOpen]);

    return (
        sector && (
            <div className="feature-viewer-feature-box sectors" id={`sector-${sectorIndex}`}>
                <Collapsible
                    title={`Sektor ${sector.color === SectorColor.DARK ? "-" : displayIndex + 1}`}
                    icon={<SectorColorIndicator color={sector.color?.valueOf() ?? SectorColor.DARK} height="1.7rem" />}
                    isOpen={isOpen}
                    setIsOpen={(isOpen) => setIsOpen(sectorIndex, isOpen)}
                >
                    {lighthouseData.sectors.length > 1 && (
                        <>
                            <SectorNumberAttribute
                                label={`Retning linje ${displayIndex + 1}`}
                                sector={sector}
                                attributeName="startAngle"
                                defaultValue={0}
                                setValue={(value: number | string, sectorIndex: number) =>
                                    setLineAngle(value as number, sectorIndex)
                                }
                                onFocus={() => onFocus()}
                                onBlur={() => onBlur()}
                                onMouseEnter={() => onMouseEnter()}
                                onMouseLeave={() => onMouseLeave()}
                                unit={"°"}
                                max={10000}
                                step="0.01"
                            />

                            <SectorNumberAttribute
                                label={`Lengde linje ${displayIndex + 1}`}
                                sector={sector}
                                attributeName="startLineLength"
                                defaultValue={DefaultSectorValues.defaultLineLength}
                                setValue={(value: number | string, sectorIndex: number) =>
                                    setLineLength(value as number, sectorIndex)
                                }
                                onFocus={() => onFocus()}
                                onBlur={() => onBlur()}
                                onMouseEnter={() => onMouseEnter()}
                                onMouseLeave={() => onMouseLeave()}
                                valueMultiplier={unitSelectedIndex === 0 ? metersPerNauticalMile : 1}
                                decimalDigits={unitSelectedIndex === 0 ? 3 : 0}
                                unit={unitSelectedIndex === 0 ? "Nm" : "m"}
                                min={0}
                                step={unitSelectedIndex === 0 ? "0.01" : "10"}
                            />

                            <Spacer height={10} />
                            <SectorNumberAttribute
                                label={`Retning linje ${
                                    displayIndex + 1 === lighthouseData.sectors.length
                                        ? (displayIndex + 2) % lighthouseData.sectors.length
                                        : displayIndex + 2
                                }`}
                                sector={sectorNext}
                                attributeName="startAngle"
                                defaultValue={0}
                                setValue={(value: number | string, sectorIndex: number) =>
                                    setLineAngle(value as number, sectorIndex)
                                }
                                onFocus={() => onFocus(sectorNext.index)}
                                onBlur={() => onBlur(sectorNext.index)}
                                onMouseEnter={() => onMouseEnter(sectorNext.index)}
                                onMouseLeave={() => onMouseLeave(sectorNext.index)}
                                errorMessage={
                                    isAngleLessThanOneDegree(sector.startAngle ?? 0, sectorNext.startAngle ?? 0)
                                        ? "Sektor mindre enn 1 grad"
                                        : undefined
                                }
                                unit={"°"}
                                max={10000}
                                step="0.01"
                            />
                            <SectorNumberAttribute
                                label={`Lengde linje ${
                                    displayIndex + 1 === lighthouseData.sectors.length
                                        ? (displayIndex + 2) % lighthouseData.sectors.length
                                        : displayIndex + 2
                                }`}
                                sector={sectorNext}
                                attributeName="startLineLength"
                                defaultValue={DefaultSectorValues.defaultLineLength}
                                setValue={(value: number | string, sectorIndex: number) =>
                                    setLineLength(value as number, sectorIndex)
                                }
                                onFocus={() => onFocus(sectorNext.index)}
                                onBlur={() => onBlur(sectorNext.index)}
                                onMouseEnter={() => onMouseEnter(sectorNext.index)}
                                onMouseLeave={() => onMouseLeave(sectorNext.index)}
                                valueMultiplier={unitSelectedIndex === 0 ? metersPerNauticalMile : 1}
                                decimalDigits={unitSelectedIndex === 0 ? 3 : 0}
                                unit={unitSelectedIndex === 0 ? "Nm" : "m"}
                                min={0}
                                step={unitSelectedIndex === 0 ? "0.01" : "10"}
                            />
                            <Spacer height={10} />
                        </>
                    )}
                    <SectorNumberAttribute
                        label="Radius"
                        sector={sector}
                        attributeName="innerRadius"
                        defaultValue={DefaultSectorValues.defaultInnerRadius}
                        setValue={(value: number | string, sectorIndex: number) =>
                            setSectorValue(value, sectorIndex, "innerRadius")
                        }
                        onBlur={() => onBlur()}
                        valueMultiplier={unitSelectedIndex === 0 ? metersPerNauticalMile : 1}
                        decimalDigits={unitSelectedIndex === 0 ? 3 : 0}
                        unit={unitSelectedIndex === 0 ? "Nm" : "m"}
                        min={0}
                        step={unitSelectedIndex === 0 ? "0.01" : "10"}
                        disabled={sector.color === SectorColor.DARK}
                    />
                    {isLighthouse(selectedFeature) && (
                        <SectorColorAttribute
                            label="Farge"
                            value={sector.color ?? SectorColor.WHITE}
                            setSelectedOption={setSelectedColorOption}
                        />
                    )}
                    <Spacer height={10} />
                    <SectorDescriptionAttribute
                        sector={sector}
                        setDescription={(description: string, sectorIndex: number) =>
                            setSectorValue(description, sectorIndex, "description")
                        }
                        initialText={lighthouseData.sectors[sectorIndex].description}
                        disabled={sector.color === SectorColor.DARK}
                    />
                    <div className="flex-row small-gap">
                        <NfsButton
                            onClick={() => {
                                if (!hasUnsavedChanges) {
                                    dispatch(setHasUnsavedChanges(true));
                                }
                                const newLighthouseData = lighthouseContext!.lighthouseData.value;
                                lighthouseContext!.lighthouseData.value = createSector(lighthouseData, sectorIndex);
                                setLighthouseState({ ...newLighthouseData });
                            }}
                            disabled={!isLighthouse(selectedFeature) && lighthouseData.sectors.length >= 2}
                        >
                            <AddIcon />
                            Ny sektor
                        </NfsButton>
                        <ConfirmationPopover
                            disabled={lighthouseData.sectors.length === 1}
                            onConfirm={() => {
                                if (!hasUnsavedChanges) {
                                    dispatch(setHasUnsavedChanges(true));
                                }
                                const deletedSector = deleteSector(lighthouseData, sectorIndex);
                                setLighthouseState({ ...deletedSector });
                                removeLighthouseEditingOverlay();
                            }}
                            confirmText="Slett"
                            title="Bekreft sletting"
                            message="Er du sikker på at du vil slette denne sektoren?"
                            warning
                            onCancel={() => {}}
                        >
                            <div className="nfs-button outline">
                                <TrashCanIcon />
                                Slett sektor
                            </div>
                        </ConfirmationPopover>
                    </div>
                </Collapsible>
            </div>
        )
    );
};

export default SectorMenu;
